import React, { FC } from 'react';
import styled from 'styled-components';
import HtmlParser from 'react-html-parser';
import { Tooltip } from './Tooltip';

type LabelWithInteractionsProps = {
  label: string;
  interactions: LabelInteraction[];
  options?: InteractionOptions;
};

export type InteractionOptions = {
  linkWeight?:
    | 100
    | 200
    | 300
    | 400
    | 500
    | 600
    | 700
    | 800
    | 900
    | 'lighter'
    | 'normal'
    | 'bold'
    | 'bolder'
    | 'inherit';
  linkColor?: string;
  linkUnderline?: 'always' | 'hover' | 'none' | 'hover-inverted';
  tooltipPlacement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top';
};

export type FrameName = string;
export type InteractionType = 'link' | 'tooltip';

export type TooltipInteraction = {
  label: string;
  content: string | React.ReactNode;
};

export type LinkInteraction = {
  label: string;
  path: string;
  target?: '_blank' | '_self' | '_parent' | '_top' | FrameName;
  rel?: string;
};

export type LabelInteraction = {
  type: InteractionType;
  data: LinkInteraction | TooltipInteraction;
};

const DEFAULT_OPTIONS: InteractionOptions = {
  linkWeight: 'inherit',
  linkColor: 'inherit',
  linkUnderline: 'hover',
  tooltipPlacement: 'top',
};

const LabelLink = styled.a`
  color: ${({ theme }) => theme.palette.primary.main};
  text-decoration: none;
`;

const LabelOptionalStyles = styled(({ linkWeight, linkColor, linkUnderline, ...restProps }) => (
  <span {...restProps} />
))`
  font-weight: ${({ linkWeight }) =>
    linkWeight != null ? linkWeight : DEFAULT_OPTIONS.linkWeight};
  color: ${({ linkColor }) => (linkColor != null ? linkColor : DEFAULT_OPTIONS.linkColor)};
  text-decoration: ${({ linkUnderline }) =>
    linkUnderline === 'always' || linkUnderline === 'hover-inverted'
      ? 'underline'
      : linkUnderline === 'none'
      ? 'none'
      : DEFAULT_OPTIONS.linkUnderline === 'hover'
      ? 'none'
      : DEFAULT_OPTIONS.linkUnderline};

  &:hover {
    text-decoration: ${({ linkUnderline }) =>
      ['hover', 'always'].includes(linkUnderline) ? 'underline' : 'none'};
  }
`;

const TooltipInnerDiv = styled.div`
  display: inline-block;
`;

export const LabelWithInteractions: FC<LabelWithInteractionsProps> = ({
  label,
  interactions,
  options = DEFAULT_OPTIONS,
}) => {
  const splitLabel = label.split('%INTERACTION%');
  const htmlNodes: React.ReactElement[] = [];
  splitLabel.forEach((val) => {
    htmlNodes.push(<>{HtmlParser(val)}</>);
  });
  const resultNodes = [];
  let numProcessed = 0;
  interactions.forEach((interaction, index) => {
    resultNodes.push(htmlNodes[index]);
    if (interaction.type === 'link') {
      const interactionData = interaction.data as LinkInteraction;
      resultNodes.push(
        <LabelLink
          href={interactionData.path}
          target={interactionData.target || '_self'}
          rel={interactionData.rel}
        >
          <LabelOptionalStyles {...options}>{interactionData.label}</LabelOptionalStyles>
        </LabelLink>
      );
    }
    if (interaction.type === 'tooltip') {
      const interactionData = interaction.data as TooltipInteraction;
      resultNodes.push(
        <Tooltip title={interactionData.content as string} placement={options.tooltipPlacement}>
          <TooltipInnerDiv>
            <LabelOptionalStyles {...options}>{interactionData.label}</LabelOptionalStyles>
          </TooltipInnerDiv>
        </Tooltip>
      );
    }

    numProcessed++;
  });
  if (numProcessed < splitLabel.length) {
    resultNodes.push(splitLabel[splitLabel.length - 1]);
  }

  return (
    <>
      {resultNodes.map((node, index) => (
        <React.Fragment key={index}>{node}</React.Fragment>
      ))}
    </>
  );
};
